import { useEffect } from 'react';
import { stagePlayer } from './stagePlayer.js';
import { UI_SFX } from '../assets/audioManifest.js';

/**
 * useUiClickSounds — one app-wide click blip.
 *
 * Wired ONCE at the App shell (not per screen) so every button, tile, medallion
 * and pill sounds the same wherever it lives. A pointerdown that lands on
 * something interactive plays `click`; one that lands on dead space plays the
 * quieter `clickEmpty` — the same gesture, told apart by what it hit.
 *
 * The listener also doubles as the audio-unlock gesture: on a cold browser tab
 * (Store, /play) the AudioContext is suspended until a real user gesture, so the
 * FIRST click unlocks it here and everything scheduled after that is audible —
 * including any menu music a screen asked for before the gesture arrived.
 *
 * Capture phase + a short rate limit: capture so a component's own
 * stopPropagation can't silence it, rate limit so multi-touch and
 * synthetic re-dispatches don't stack blips.
 */

/** Anything that looks like a control. Kept loose on purpose — a missed blip
 *  reads as a broken button, an extra one just reads as feedback. */
const INTERACTIVE_SELECTOR = [
  'button',
  'a[href]',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  '[role="button"]',
  '[role="option"]',
  '[role="tab"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[contenteditable="true"]',
  '[data-ui-click]',
].join(',');

/** Ignore repeats inside this window (multi-touch / re-dispatch). */
const MIN_GAP_MS = 70;

export function useUiClickSounds(): void {
  useEffect(() => {
    let last = 0;
    // Warm the decode cache the moment audio is available so the first click is
    // heard, not swallowed by an in-flight fetch.
    const unsubscribe = stagePlayer.onUnlocked(() => {
      stagePlayer.preload([UI_SFX.click, UI_SFX.clickEmpty]);
    });

    const onDown = (e: PointerEvent) => {
      // The gesture itself is what browsers accept as an unlock — take it.
      stagePlayer.unlock();
      const now = Date.now();
      if (now - last < MIN_GAP_MS) return;
      last = now;
      const target = e.target instanceof Element ? e.target : null;
      const interactive = !!target?.closest(INTERACTIVE_SELECTOR);
      // Dead space is quieter — it acknowledges the tap without rewarding it.
      if (interactive) stagePlayer.playUi(UI_SFX.click);
      else stagePlayer.playUi(UI_SFX.clickEmpty, 0.55);
    };

    document.addEventListener('pointerdown', onDown, { capture: true, passive: true });
    return () => {
      unsubscribe();
      document.removeEventListener('pointerdown', onDown, { capture: true });
    };
  }, []);
}
